import { useCallback, useMemo, useRef, useState } from 'react'
import { RemoteTableRef } from 'medere-ui-framework'
import {
  OperatingRoomFilterDTO,
  OperatingRoomHeatCellDTO,
  OperatingRoomHeatMeasure,
  OperatingRoomOutcome,
} from '../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import {
  OperatingRoomTableParams,
  isAccessDenied,
  isRequestCanceled,
  isSurgeonAccessDenied,
} from '../../services/OperatingRoomIndicatorsController'

/** Resultado de la última consulta de la grilla: `error` y `denied` no son una grilla vacía. */
export type OperatingRoomGridState = 'ok' | 'error' | 'denied' | 'surgeonDenied'

/** Qué muestra la lista además de los filtros de pantalla: el estado de las cirugías y, si vino del mapa de calor, su celda. */
export interface OperatingRoomListView {
  outcome: OperatingRoomOutcome
  cell: (OperatingRoomHeatCellDTO & { measure: OperatingRoomHeatMeasure }) | null
}

/**
 * Lo común a las grillas de la pantalla (hoy, solo la lista): le da a la tabla los filtros de pantalla en
 * `params` (llegan a `getItems` junto con los de columna y el orden), distingue el error y el «sin permiso» de una grilla
 * vacía y avisa de cada pedido de la tabla (`onLoad`): lo último que pidió es lo que ve el usuario y, por lo tanto, lo que
 * exporta «Exportar». Los filtros y el orden de la tabla quedan en el navegador mientras se cambia de pestaña; la pantalla
 * los borra al abrirse y al cerrarse.
 *
 * @param filters filtros aplicados de la pantalla (período y «Más filtros»).
 * @param view estado de las cirugías y celda del mapa de calor: llegan a `getItems` junto con los filtros.
 * @param load pide una página al backend.
 * @param onLoad avisa de cada pedido de la tabla (para guardarlo como lo último que se vio).
 */
export default function useOperatingRoomGrid<TRow>(
  filters: OperatingRoomFilterDTO,
  view: OperatingRoomListView,
  load: (offSet: number, pageSize: number, params: OperatingRoomTableParams) => Promise<TRow[]>,
  onLoad: (params: OperatingRoomTableParams) => void,
) {
  const tableRef = useRef<RemoteTableRef>(null)
  const [state, setState] = useState<OperatingRoomGridState>('ok')

  /** Los filtros de la pantalla llegan a `getItems` junto con los de columna y el orden de la tabla. */
  const params = useMemo<OperatingRoomTableParams>(
    () => ({
      filters,
      outcome: view.outcome,
      ...(view.cell ? { cell: { dayOfWeek: view.cell.dayOfWeek, hour: view.cell.hour }, measure: view.cell.measure } : {}),
    }),
    [filters, view.outcome, view.cell],
  )

  const loadItems = useCallback(async (offSet: number, pageSize: number, tableParams?: OperatingRoomTableParams): Promise<TRow[]> => {
    const request = tableParams ?? {}
    onLoad(request)
    try {
      const rows = await load(offSet, pageSize, request)
      setState('ok')
      return rows
    } catch (error: unknown) {
      if (!isRequestCanceled(error)) {
        setState(isSurgeonAccessDenied(error) ? 'surgeonDenied' : isAccessDenied(error) ? 'denied' : 'error')
      }
      return []
    }
  }, [load, onLoad])

  const retry = useCallback(() => tableRef.current?.refreshTable?.(), [])

  return { tableRef, params, loadItems, state, retry }
}
